// SEAT-DRAWER-UX §1.2 (docs/design/2026-10-28-seat-drawer-ux.md): one row, one home. The seat's
// scorecard rows split three ways, the way the scorecard engine already scores them apart:
//   - KPI rows (Performance): scored, `kpi_weight > 0` — the KPI score is the weighted share met;
//   - goal rows (Execution): scored, `goal_weight > 0` — the goal score is the weighted mean of
//     attainment; a row with both weights is in both;
//   - record rows (Record, DE seats): the DE metric catalog (`de.*` keys), wherever they score;
//     they leave Performance and Execution, which say in one line how many of them count.
// `context` and `off` rows show nowhere in the drawer but the record (a `de.*` context row is the
// record's content); the KPI editor still lists them.
import { FAILURE_METRIC_KEYS } from "@companyos/schema/de-metric-catalog";
import type { ScorecardMetric } from "../../api/types";

/** A key from the DE metric catalog (`packages/schema/src/de-metric-catalog.ts`). */
export const isRecordKey = (key: string) => key.startsWith("de.");

type Row = Pick<
  ScorecardMetric,
  "metric_def_id" | "key" | "name" | "tracking_mode" | "kpi_weight" | "goal_weight"
>;

const byWeight =
  <T extends Row>(weight: (m: T) => number) =>
  (a: T, b: T) =>
    weight(b) - weight(a) || a.name.localeCompare(b.name);

/** Performance: the scored KPI rows, heaviest first. */
export function kpiRows<T extends Row>(metrics: readonly T[]): T[] {
  return metrics
    .filter((m) => !isRecordKey(m.key) && m.tracking_mode === "scored" && m.kpi_weight > 0)
    .sort(byWeight((m) => m.kpi_weight));
}

/** Execution: the scored goal rows, heaviest goal weight first. */
export function goalRows<T extends Row>(metrics: readonly T[]): T[] {
  return metrics
    .filter((m) => !isRecordKey(m.key) && m.tracking_mode === "scored" && m.goal_weight > 0)
    .sort(byWeight((m) => m.goal_weight));
}

/** Record: the DE catalog rows the seat tracks (scored or context), heaviest KPI weight first. */
export function recordRows<T extends Row>(metrics: readonly T[]): T[] {
  return metrics
    .filter((m) => isRecordKey(m.key) && m.tracking_mode !== "off")
    .sort(byWeight((m) => m.kpi_weight));
}

/**
 * FAILURE-STOP: the failure KPIs (tool errors per run, runs stopped after repeated failures,
 * failure rate, tokens in failed or stopped runs). Record rows at weight 0, also shown under
 * Performance so a stop is seen where the score is read; catalog order.
 */
export function failureRows<T extends Row>(metrics: readonly T[]): T[] {
  const order = FAILURE_METRIC_KEYS as readonly string[];
  return metrics
    .filter((m) => order.includes(m.key) && m.tracking_mode !== "off")
    .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
}

/** How many record rows count in the KPI score (Performance's "+N agent-record metrics" line). */
export function scoredRecordCount(metrics: readonly Row[]): number {
  return metrics.filter(
    (m) => isRecordKey(m.key) && m.tracking_mode === "scored" && m.kpi_weight > 0,
  ).length;
}

/** The scored KPI rows that may be weighted as a goal (Execution's goal picker). */
export function goalCandidates<T extends Row>(metrics: readonly T[]): T[] {
  return metrics
    .filter((m) => !isRecordKey(m.key) && m.tracking_mode === "scored" && m.goal_weight === 0)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The "Weight N" a row carries when its weight in this list is not the default 1. */
export function weightChip(
  m: Pick<Row, "kpi_weight" | "goal_weight">,
  home: "performance" | "execution" | "record" = "performance",
): string | null {
  const w = home === "execution" ? m.goal_weight : m.kpi_weight;
  return w > 0 && w !== 1 ? `Weight ${w}` : null;
}

/** The All view's rows per section: the first few, and how many more the tab holds. */
export const ALL_VIEW_ROWS = 4;
export function topOf<T>(rows: readonly T[], all: boolean): { shown: T[]; more: number } {
  if (!all) return { shown: [...rows], more: 0 };
  return { shown: rows.slice(0, ALL_VIEW_ROWS), more: Math.max(0, rows.length - ALL_VIEW_ROWS) };
}
